/**
 * SeverityDistributionCard Component
 * Phase 7: Displays severity distribution of findings with drill-down links.
 */

import { useNavigate } from 'react-router-dom'
import { SeverityBadge } from './SeverityBadge'
import { SeverityDistribution } from '../lib/api'
import { useLocale } from '../contexts/LocaleContext'

export interface SeverityDistributionCardProps {
  /** Severity distribution data */
  distribution: SeverityDistribution
  
  /** Audit run ID for drill-down links */
  auditRunId: string
  
  /** Optional title override */
  title?: string
}

interface SeverityItem {
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info'
  count: number
  labelKey: string
}

export function SeverityDistributionCard({
  distribution,
  auditRunId,
  title,
}: SeverityDistributionCardProps) {
  const navigate = useNavigate()
  const { t } = useLocale()
  
  const items: SeverityItem[] = [
    { severity: 'critical', count: distribution.critical, labelKey: 'scores.severityCritical' },
    { severity: 'high', count: distribution.high, labelKey: 'scores.severityHigh' },
    { severity: 'medium', count: distribution.medium, labelKey: 'scores.severityMedium' },
    { severity: 'low', count: distribution.low, labelKey: 'scores.severityLow' },
    { severity: 'info', count: distribution.info, labelKey: 'scores.severityInfo' },
  ]
  
  const handleSeverityClick = (severity: string) => {
    navigate(`/findings?audit_run_id=${auditRunId}&severity=${severity}`)
  }
  
  return (
    <div className="card" data-testid="severity-distribution-card">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)]">
          {title || t('scores.severityDist')}
        </h3>
        <p className="text-xs text-[var(--text-secondary)] mt-1">
          {t('scores.totalFindings', { n: distribution.total })}
        </p>
      </div>
      
      <div className="space-y-3">
        {items.map(({ severity, count, labelKey }) => {
          const label = t(labelKey)
          return (
          <a
            key={severity}
            href={`/findings?audit_run_id=${auditRunId}&severity=${severity}`}
            className="severity-row flex items-center gap-3 hover:bg-[var(--surface-secondary)] p-2 -mx-2 rounded transition-colors no-underline focus:outline-none focus:ring-2 focus:ring-[var(--chip-color,var(--reviewer-accent))]"
            style={{ ['--chip-color' as string]: `var(--reviewer-severity-${severity})` }}
            onClick={(e) => {
              e.preventDefault()
              handleSeverityClick(severity)
            }}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault()
                handleSeverityClick(severity)
              }
            }}
            aria-label={t('scores.viewSeverityAria', { count, label })}
          >
            <div className="flex-shrink-0">
              <SeverityBadge severity={severity} />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[var(--text-primary)]">
                  {count}
                </span>
                <span className="text-xs text-[var(--text-secondary)]">
                  {t('scores.findingsWord')}
                </span>
              </div>
              
              {distribution.total > 0 && (
                <div className="mt-1 w-full h-1 bg-[var(--surface-secondary)] rounded-sm overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300`}
                    style={{
                      width: `${(count / distribution.total) * 100}%`,
                      backgroundColor: `var(--severity-${severity})`,
                    }}
                    role="progressbar"
                    aria-valuenow={count}
                    aria-valuemin={0}
                    aria-valuemax={distribution.total}
                    aria-label={`${label}: ${count} / ${distribution.total}`}
                  />
                </div>
              )}
            </div>
            
            <svg
              className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
          )
        })}
      </div>
      
      {distribution.total === 0 && (
        <div className="text-center py-8 text-sm text-[var(--text-tertiary)]">
          {t('scores.noFindingsRecorded')}
        </div>
      )}
    </div>
  )
}
